import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import useDebounce from "../../../../hooks/useDebounce";
import messages from "../../../../assets/locale/messages";
import { companyPermissionModules } from "../../../../utils/Constants";
import { canAddCompanyModule } from "../../../../utils/Permissions";
import { ROUTE_PATHS } from "../../../../utils/RoutesPaths";
import {
  createCompanyLogisticsIssueRequest,
  getCompanyLogisticsIssuesRequest,
} from "../../../../store/CompanyPortal/LogisticsIssues/actions";
import * as companyPortalEventsApi from "../../../../network/apis/companyPortalEvents";
import {
  getPortalApiErrorMessage,
  unwrapPortalEventEnvelope,
} from "../../../../utils/companyPortal/eventApi";
import { flattenCompanyPortalEventsList } from "../../../../utils/companyPortal/portalEventsList";
import { showErrorMsg } from "../../../../utils/Helpers";
import {
  buildIssueImagesAttributes,
} from "../../../../components/Events/Event/Reports/reportUpload";
import {
  buildCompanyReportsListParams,
  mapReportRow,
} from "../../../../components/Events/Event/Reports/reportMappers";

const SEARCH_DEBOUNCE_MS = 300;

const useReportsList = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const lang = useSelector((state) => state.locale.lang);
  const list = useSelector((state) => state.companyLogisticsIssues.list);
  const meta = useSelector((state) => state.companyLogisticsIssues.meta);
  const isLoading = useSelector(
    (state) => state.companyLogisticsIssues.isLoading,
  );
  const creating = useSelector((state) => state.companyLogisticsIssues.creating);
  const login = useSelector((state) => state.auth.login);
  const { report, shared } = messages[lang];

  const canAdd = canAddCompanyModule(
    companyPermissionModules.reports,
    login,
  );

  const [searchValue, setSearchValue] = useState("");
  const debouncedSearch = useDebounce(searchValue, SEARCH_DEBOUNCE_MS);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [eventFilter, setEventFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [eventOptions, setEventOptions] = useState([]);
  const [zoneOptions, setZoneOptions] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState(null);

  const listParams = useMemo(
    () =>
      buildCompanyReportsListParams({
        search: debouncedSearch,
        page,
        pageSize,
        eventId: eventFilter?.id,
        status: statusFilter?.id,
      }),
    [debouncedSearch, page, pageSize, eventFilter, statusFilter],
  );

  useEffect(() => {
    dispatch(getCompanyLogisticsIssuesRequest(listParams));
  }, [dispatch, listParams, lang]);

  const hasActiveFilters = Boolean(
    debouncedSearch.trim() || eventFilter || statusFilter,
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await companyPortalEventsApi.getCompanyPortalEventsList({
          page: -1,
        });
        if (cancelled) return;
        const items = flattenCompanyPortalEventsList(response);
        setEventOptions(
          items.map((event) => ({
            id: event.id,
            name: event.name || String(event.id),
          })),
        );
      } catch (error) {
        if (!cancelled) {
          showErrorMsg(
            getPortalApiErrorMessage(error, shared.generalErrMsg),
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [shared.generalErrMsg]);

  useEffect(() => {
    if (!selectedEventId) {
      setZoneOptions([]);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await companyPortalEventsApi.getCompanyPortalEvent(
          selectedEventId,
        );
        if (cancelled) return;
        const unwrapped = unwrapPortalEventEnvelope(res);
        const attrs = unwrapped?.attributes ?? {};
        const zones =
          attrs.zones_attributes || attrs.zones || unwrapped?.zones || [];
        setZoneOptions(
          (Array.isArray(zones) ? zones : []).map((zone) => ({
            id: zone.id,
            name: zone.name || zone.attributes?.name || String(zone.id),
          })),
        );
      } catch (error) {
        if (!cancelled) {
          setZoneOptions([]);
          showErrorMsg(
            getPortalApiErrorMessage(error, shared.generalErrMsg),
          );
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedEventId, shared.generalErrMsg]);

  const rows = useMemo(
    () =>
      (Array.isArray(list) ? list : []).map((issue) =>
        mapReportRow(issue, report.reporterPositions),
      ),
    [list, report.reporterPositions],
  );
  const totalCount = meta.count || 0;
  const paginationPages =
    meta.pages || Math.max(Math.ceil(totalCount / pageSize), 1);
  const paginationFrom = totalCount > 0 ? (page - 1) * pageSize + 1 : 0;
  const paginationTo = Math.min(page * pageSize, totalCount);
  const statusOptions = useMemo(
    () => [
      { id: "open", name: report.listReport.open },
      { id: "closed", name: report.listReport.closed },
    ],
    [report.listReport.closed, report.listReport.open],
  );

  const addModalLabels = useMemo(
    () => ({
      title: report.addReport.title,
      description: report.addReport.description,
      cancel: shared.cancel,
      submit: report.addReport.submit,
      reportTitle: report.addReport.reportTitle,
      reportTitlePlaceholder: report.addReport.reportTitlePlaceholder,
      event: report.addReport.event,
      eventPlaceholder: report.addReport.eventPlaceholder,
      zone: report.addReport.zone,
      zonePlaceholder: report.addReport.zonePlaceholder,
      issueDescription: report.addReport.issueDescription,
      issueDescriptionPlaceholder: report.addReport.issueDescriptionPlaceholder,
      descriptionHint: report.addReport.descriptionHint,
      descriptionMaxLengthError: report.addReport.descriptionMaxLengthError,
      open: report.listReport.open,
      closed: report.listReport.closed,
      reportStatus: report.addReport.reportStatus,
      uploadLabel: report.addReport.uploadLabel,
      uploadClickToUpload: report.addReport.uploadClickToUpload,
      uploadDragHint: report.addReport.uploadDragHint,
      uploadHint: report.addReport.uploadHint,
      uploadFormatHint: report.addReport.uploadFormatHint,
      uploadRemainingHint: report.addReport.uploadRemainingHint,
      uploadLimitReached: report.addReport.uploadLimitReached,
      uploadMaxSizeError: report.addReport.uploadMaxSizeError,
      uploadTypeError: report.addReport.uploadTypeError,
      required: report.addReport.required,
    }),
    [report, shared.cancel],
  );

  const handleSearchChange = useCallback((value) => {
    setSearchValue(value);
    setPage(1);
  }, []);

  const handleEventFilterChange = useCallback((value) => {
    setEventFilter(value);
    setPage(1);
  }, []);

  const handleStatusFilterChange = useCallback((value) => {
    setStatusFilter(value);
    setPage(1);
  }, []);

  const handlePageChange = useCallback((nextPage) => {
    setPage(nextPage);
  }, []);

  const handlePageSizeChange = useCallback((nextSize) => {
    setPageSize(nextSize);
    setPage(1);
  }, []);

  const handleOpenAddModal = useCallback(() => {
    setSelectedEventId(null);
    setZoneOptions([]);
    setShowAddModal(true);
  }, []);

  const handleCloseAddModal = useCallback(() => {
    setShowAddModal(false);
    setSelectedEventId(null);
    setZoneOptions([]);
  }, []);

  const handleCreateReport = useCallback(
    async (formValues) => {
      try {
        const imageUrls =
          formValues.imageUrls ||
          (formValues.imageUrl ? [formValues.imageUrl] : []);
        const data = {
          event_id: formValues.eventId,
          zone_id: formValues.zoneId,
          title: formValues.title,
          description: formValues.description,
          status: formValues.status,
        };
        const images = buildIssueImagesAttributes(imageUrls);
        if (images.length) {
          data.logistics_issue_images_attributes = images;
        }

        dispatch(
          createCompanyLogisticsIssueRequest({
            data,
            onSuccess: () => {
              handleCloseAddModal();
              dispatch(getCompanyLogisticsIssuesRequest(listParams));
            },
          }),
        );
      } catch (error) {
        // saga handle messaging
      }
    },
    [dispatch, handleCloseAddModal, listParams],
  );

  const handleRowClick = useCallback(
    (row) => {
      if (!row?.id) return;
      history.push(ROUTE_PATHS.companyViewReport.replace(":id", row.id));
    },
    [history],
  );

  return {
    title: shared.reports,
    breadcrumbLabel: shared.reports,
    canAdd,
    addReportLabel: report.addReport.addNewReport,
    searchPlaceholder: report.searchPlaceholder,
    searchValue,
    onSearchChange: handleSearchChange,
    eventFilter,
    eventOptions,
    onEventFilterChange: handleEventFilterChange,
    statusFilter,
    statusOptions,
    onStatusFilterChange: handleStatusFilterChange,
    rows,
    page,
    pageSize,
    paginationPages,
    paginationFrom,
    paginationTo,
    totalCount,
    showingRangeTemplate: report.showingRange,
    rowsPerPageLabel: shared.numberOfRows,
    onPageChange: handlePageChange,
    onPageSizeChange: handlePageSizeChange,
    emptyTitle: hasActiveFilters ? report.noResultsTitle : report.emptyTitle,
    emptyDescription: hasActiveFilters
      ? report.noResultsDescription
      : report.emptyDescription,
    emptyActionLabel: report.addReport.addNewReport,
    hasActiveFilters,
    isLoading,
    showAddModal,
    onOpenAddModal: handleOpenAddModal,
    onCloseAddModal: handleCloseAddModal,
    onCreateReport: handleCreateReport,
    creating,
    addModalLabels,
    addEventOptions: eventOptions,
    addZoneOptions: zoneOptions,
    onAddEventChange: setSelectedEventId,
    tableLabels: {
      reportTitle: report.listReport.reportTitle,
      eventName: report.listReport.eventName,
      zone: report.listReport.zone,
      name: report.listReport.name,
      role: report.listReport.role,
      dateTime: report.listReport.dateTime,
      status: report.listReport.status,
      open: report.listReport.open,
      closed: report.listReport.closed,
    },
    onRowClick: handleRowClick,
  };
};

export default useReportsList;
